import {
  HTTP_INTERCEPTORS,
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpParams,
  HttpRequest,
} from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { from, Observable, throwError } from 'rxjs';
import { catchError, mergeMap } from 'rxjs/operators';
import { AuthService } from './services/auth.service';
import { WorkingLaboratoryService } from './services/prothesis-api/working-laboratory.service';
import { WorkingCustomerService } from './services/prothesis-customer-api/working-customer.service';

// From https://medium.com/@MetonymyQT/angular-http-interceptors-what-are-they-and-how-to-use-them-52e060321088
@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private static readonly customerAreaMessageRegex: RegExp = /^\/customer\/api\/CustomerAreaMessage\/?$/;
  private static readonly customerGetInfoRegex: RegExp =
    /^\/customer\/api\/Customer\/[0-9A-F]{8}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{12}\/Customer\/GetInfo\/?$/i;
  private static readonly getAllowedTenantsRegex: RegExp = /^\/api\/authenticated\/User\/GetAllowedTenants\/?$/;
  private static readonly laboratoryAreaMessageRegex: RegExp = /^\/api\/LaboratoryAreaMessage\/?$/;
  private static readonly laboratoryDirectoryLineRegex: RegExp =
    /^\/api\/Laboratory\/[0-9A-F]{8}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{12}\/DirectoryLine\//i;
  private static readonly laboratoryGetInfoRegex: RegExp =
    /^\/api\/Laboratory\/[0-9A-F]{8}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{12}\/GetInfo\/?$/i;
  private static readonly laboratoryPeppolDirectoryRegex: RegExp =
    /^\/api\/Laboratory\/[0-9A-F]{8}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{12}\/PeppolDirectory\//i;

  private _authService = inject(AuthService);
  private _workingCustomerService = inject(WorkingCustomerService);
  private _workingLaboratoryService = inject(WorkingLaboratoryService);

  public intercept(httpRequest: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (httpRequest.url.startsWith('http')) {
      const requestUrl = new URL(httpRequest.url);
      if (
        requestUrl.pathname.indexOf('/api/') === 0 ||
        requestUrl.pathname.indexOf('/identity/api/') === 0 ||
        requestUrl.pathname.indexOf('/customer/api/') === 0
      ) {
        if (
          this._authService.isAdminOrSuperAdminFlag &&
          !this._workingLaboratoryService.tenantUuid &&
          !requestUrl.pathname.startsWith('/api/admin') &&
          !requestUrl.pathname.startsWith('/identity/api') &&
          !AuthInterceptor.laboratoryAreaMessageRegex.test(requestUrl.pathname) &&
          !AuthInterceptor.customerAreaMessageRegex.test(requestUrl.pathname) &&
          !AuthInterceptor.getAllowedTenantsRegex.test(requestUrl.pathname) &&
          !AuthInterceptor.laboratoryGetInfoRegex.test(requestUrl.pathname) &&
          !AuthInterceptor.laboratoryPeppolDirectoryRegex.test(requestUrl.pathname) &&
          !AuthInterceptor.laboratoryDirectoryLineRegex.test(requestUrl.pathname)
        ) {
          // dans l'url si tenantuuid est là mais pas défini
          console.log('Vous devez choisir un laboratoire à explorer avant de pouvoir accéder à ses données.');
          const response = new HttpErrorResponse({
            status: -1,
            statusText: 'Canceled because laboratoryUuid is undefined',
            url: httpRequest.url,
          });

          return throwError(response);
        }

        if (
          !this._workingLaboratoryService.tenantUuid &&
          !requestUrl.pathname.startsWith('/api/admin') &&
          !requestUrl.pathname.startsWith('/identity/api') &&
          !AuthInterceptor.laboratoryAreaMessageRegex.test(requestUrl.pathname) &&
          !AuthInterceptor.customerAreaMessageRegex.test(requestUrl.pathname) &&
          !AuthInterceptor.getAllowedTenantsRegex.test(requestUrl.pathname) &&
          !AuthInterceptor.laboratoryGetInfoRegex.test(requestUrl.pathname) &&
          !AuthInterceptor.customerGetInfoRegex.test(requestUrl.pathname)
        ) {
          console.error(
            'AuthInterceptor: TenantUuid is null or undefined.',
            'URL',
            httpRequest.url,
            'tenantUuid:',
            this._workingLaboratoryService.tenantUuid,
            'isAuthenticated:',
            this._authService.isAuthenticated,
            'userUuid:',
            this._authService.userUuid,
            'isSuperAdminFlag:',
            this._authService.isSuperAdminFlag,
            'isAdminOrSuperAdminFlag:',
            this._authService.isAdminOrSuperAdminFlag,
            'exploringFlag:',
            this._workingLaboratoryService.exploringFlag,
            'laboratoryCode:',
            this._workingLaboratoryService.laboratoryCode,
            'identityServerAddress:',
            this._authService.identityServerAddress,
          );
          const response = new HttpErrorResponse({
            status: -1,
            statusText: 'Canceled because laboratoryUuid is undefined',
            url: httpRequest.url,
          });

          return throwError(response);
        }

        if (
          !this._workingCustomerService.tenantUuid &&
          requestUrl.pathname.startsWith('/customer/api') &&
          !requestUrl.pathname.startsWith('/customer/api/Customer/authenticated/Customer/InitializePicker') &&
          !AuthInterceptor.customerAreaMessageRegex.test(requestUrl.pathname) &&
          !AuthInterceptor.customerGetInfoRegex.test(requestUrl.pathname)
        ) {
          // dans l'url si tenantuuid est là mais pas défini
          console.log('Vous devez choisir un client à explorer avant de pouvoir accéder à ses données.');
          const response = new HttpErrorResponse({
            status: -2,
            statusText: 'Canceled because customerUuid is undefined',
            url: httpRequest.url,
          });

          return throwError(response);
        }

        if (this._authService.isAccessTokenAboutToExpire) {
          return from(this._authService.refresh()).pipe(
            catchError((error) => {
              console.warn('Silent refresh failed, redirecting to login', error);
              this._authService.login();
              return throwError(
                new HttpErrorResponse({
                  status: -3,
                  statusText: 'Authentication required after failed token refresh',
                  url: httpRequest.url,
                }),
              );
            }),
            mergeMap(() => {
              const authRequest: HttpRequest<unknown> = this.setAuthHeaders(httpRequest);
              return next.handle(authRequest);
            }),
          );
        }
        const authRequest: HttpRequest<unknown> = this.setAuthHeaders(httpRequest);
        return next.handle(authRequest);
      }
    }

    return next.handle(httpRequest);
  }

  public setAuthHeaders(httpRequest: HttpRequest<unknown>): HttpRequest<unknown> {
    let headers = httpRequest.headers;

    // Don't set the Content-Type and Accept if the body a FormData (in case of a file upload for instance)
    if (!(httpRequest.body instanceof FormData)) {
      headers = headers.append('Content-Type', 'application/json');
    }

    headers = headers.append('Accept', 'application/json');

    // If an access token is set, send it as part of the request.
    const accessToken = this._authService.accessToken;
    if (accessToken) {
      const authorizationValue = 'Bearer ' + accessToken;
      headers = headers.append('Authorization', authorizationValue);
    }

    const httpParams: HttpParams = httpRequest.params;
    const authReq = httpRequest.clone({
      headers: headers,
      params: httpParams,
    });

    return authReq;
  }
}

export const authInterceptorProvider = [{ provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true }];
